import React, { useState } from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IconButton,
    PrimaryButton,
    SearchBox,
    Separator,
    Stack,
} from '@fluentui/react';
import Text from '../../../../typography/text';
import Label from '../../../../typography/label';
import OfferStorageService, { ISavedOfferHistoryData } from '../../../../../services/offers/offerStorage';
import moment from 'moment';

interface IOfferHistoryModalProps {
    isOpen: boolean;
    activeStep: string;
    onDismiss(): void;
    onSelect(selected: ISavedOfferHistoryData, target: 'all' | 'customer' | 'freight'): void;
    onHistoryListChanged?(): void;
}

const OfferHistoryModal: React.FC<IOfferHistoryModalProps> = (props) => {
    const [search, setSearch] = useState<string>('');
    const [historyList, setHistoryList] = useState<ISavedOfferHistoryData[]>(OfferStorageService.getList());

    const refreshList = () => {
        const updated = OfferStorageService.getList();
        setHistoryList(updated);
        if (props.onHistoryListChanged) {
            props.onHistoryListChanged();
        }
    };

    const handleDeleteItem = (id?: string) => {
        if (!id) return;
        OfferStorageService.remove(id);
        refreshList();
    };

    const handleClearAll = () => {
        OfferStorageService.clear();
        refreshList();
        props.onDismiss();
    };

    const filtered = historyList.filter(item => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const companyName = item.customerDetails?.company?.name?.toLowerCase() || '';
        const picName = item.customerDetails?.name?.toLowerCase() || item.customerDetails?.customer?.name?.toLowerCase() || '';
        const subsidiaryName = item.orderDetails?.subsidiary?.name?.toLowerCase() || '';
        const freightCat = item.freightDetails?.freightCategory?.name?.toLowerCase() || '';
        const originAddr = (item.addressDetails?.originAddresses || []).map(a => `${a.city || ''} ${a.province || ''} ${a.address || ''}`).join(' ').toLowerCase();
        const destAddr = (item.addressDetails?.destinationAddresses || []).map(a => `${a.city || ''} ${a.province || ''} ${a.address || ''}`).join(' ').toLowerCase();

        return companyName.includes(q) || picName.includes(q) || subsidiaryName.includes(q) || freightCat.includes(q) || originAddr.includes(q) || destAddr.includes(q);
    });

    const getOriginSummary = (item: ISavedOfferHistoryData) => {
        const list = item.addressDetails?.originAddresses || [];
        if (list.length === 0) return '-';
        const first = list[0];
        return first.city ? `${first.city} (${first.type.toUpperCase()})` : first.address || '-';
    };

    const getDestinationSummary = (item: ISavedOfferHistoryData) => {
        const list = item.addressDetails?.destinationAddresses || [];
        if (list.length === 0) return '-';
        const first = list[0];
        return first.city ? `${first.city} (${first.type.toUpperCase()})` : first.address || '-';
    };

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={props.onDismiss}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Pilih Riwayat Customer & Penawaran Sebelumnya',
                subText: 'Pilih salah satu customer yang pernah Anda input sebelumnya untuk mengisi form penawaran secara instan.',
            }}
            modalProps={{
                isBlocking: false,
                styles: {
                    main: {
                        maxWidth: '750px !important',
                        width: '750px',
                        borderRadius: 12,
                    }
                }
            }}
        >
            <Stack tokens={{ childrenGap: 14 }} styles={{ root: { maxHeight: '60vh', minHeight: '300px' } }}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 12 }}>
                    <Stack.Item grow={1}>
                        <SearchBox
                            placeholder="Cari nama PT, PIC, rute, atau kategori..."
                            value={search}
                            onChange={(_, val) => setSearch(val || '')}
                            styles={{ root: { borderRadius: 6 } }}
                        />
                    </Stack.Item>
                    {historyList.length > 0 && (
                        <DefaultButton
                            text="Hapus Semua Riwayat"
                            iconProps={{ iconName: 'Delete' }}
                            onClick={handleClearAll}
                            styles={{
                                root: { color: '#dc2626', borderColor: '#fca5a5' },
                                rootHovered: { backgroundColor: '#fee2e2', color: '#b91c1c' }
                            }}
                        />
                    )}
                </Stack>

                <Stack tokens={{ childrenGap: 10 }} styles={{ root: { overflowY: 'auto', paddingRight: 4 } }}>
                    {filtered.length === 0 ? (
                        <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { padding: '40px 20px', color: '#6b7280' } }}>
                            <Text size="medium" style={{ color: '#9ca3af' }}>
                                {historyList.length === 0 ? 'Belum ada riwayat penawaran yang tersimpan.' : 'Tidak ditemukan riwayat yang sesuai pencarian.'}
                            </Text>
                        </Stack>
                    ) : (
                        filtered.map((item, idx) => {
                            const company = item.customerDetails?.company?.name || 'Tanpa Perusahaan';
                            const pic = item.customerDetails?.name || item.customerDetails?.customer?.name || '-';
                            const subsidiary = item.orderDetails?.subsidiary?.name || '-';
                            const freight = item.freightDetails?.freightCategory?.name || '-';
                            const origin = getOriginSummary(item);
                            const destination = getDestinationSummary(item);
                            const timeAgo = moment(item.savedAt).fromNow();

                            return (
                                <Stack
                                    key={item.id || idx}
                                    styles={{
                                        root: {
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 8,
                                            padding: '12px 16px',
                                            backgroundColor: '#ffffff',
                                            transition: 'all 0.15s ease',
                                            ':hover': {
                                                borderColor: '#16a34a',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                                backgroundColor: '#fafdfa'
                                            }
                                        }
                                    }}
                                    tokens={{ childrenGap: 8 }}
                                >
                                    <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                                        <Stack tokens={{ childrenGap: 2 }}>
                                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                                <Label size="medium" style={{ fontWeight: 600, color: '#111827' }}>
                                                    {company}
                                                </Label>
                                                {idx === 0 && (
                                                    <span style={{
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                        color: '#15803d',
                                                        backgroundColor: '#dcfce7',
                                                        padding: '1px 6px',
                                                        borderRadius: 4
                                                    }}>
                                                        Terbaru
                                                    </span>
                                                )}
                                            </Stack>
                                            <Text size="small" style={{ color: '#4b5563' }}>
                                                PIC: <strong>{pic}</strong> &bull; Subsidiary: {subsidiary}
                                            </Text>
                                        </Stack>
                                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                            <Text size="xsmall" style={{ color: '#9ca3af' }}>
                                                {timeAgo}
                                            </Text>
                                            <IconButton
                                                iconProps={{ iconName: 'Delete' }}
                                                title="Hapus riwayat ini"
                                                onClick={() => handleDeleteItem(item.id)}
                                                styles={{
                                                    root: { color: '#9ca3af', width: 28, height: 28 },
                                                    rootHovered: { color: '#dc2626', backgroundColor: '#fee2e2' }
                                                }}
                                            />
                                        </Stack>
                                    </Stack>

                                    <Separator styles={{ root: { padding: '2px 0' } }} />

                                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 8 }}>
                                        <Stack tokens={{ childrenGap: 2 }}>
                                            <Text size="xsmall" style={{ color: '#6b7280' }}>
                                                Rute: <strong>{origin} &rarr; {destination}</strong>
                                            </Text>
                                            <Text size="xsmall" style={{ color: '#6b7280' }}>
                                                Kategori: {freight}
                                            </Text>
                                        </Stack>

                                        <Stack horizontal tokens={{ childrenGap: 6 }}>
                                            <DefaultButton
                                                text="Salin Customer"
                                                styles={{
                                                    root: { height: 28, padding: '0 8px', fontSize: 12 },
                                                    rootHovered: { color: '#15803d', borderColor: '#16a34a' }
                                                }}
                                                onClick={() => {
                                                    props.onSelect(item, 'customer');
                                                    props.onDismiss();
                                                }}
                                            />
                                            <DefaultButton
                                                text="Salin Rute"
                                                styles={{
                                                    root: { height: 28, padding: '0 8px', fontSize: 12 },
                                                    rootHovered: { color: '#0369a1', borderColor: '#0284c7' }
                                                }}
                                                onClick={() => {
                                                    props.onSelect(item, 'freight');
                                                    props.onDismiss();
                                                }}
                                            />
                                            <PrimaryButton
                                                text="Gunakan Semua"
                                                styles={{
                                                    root: { height: 28, padding: '0 10px', fontSize: 12, backgroundColor: '#16a34a', borderColor: '#16a34a' },
                                                    rootHovered: { backgroundColor: '#15803d', borderColor: '#15803d' }
                                                }}
                                                onClick={() => {
                                                    props.onSelect(item, 'all');
                                                    props.onDismiss();
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                </Stack>
                            );
                        })
                    )}
                </Stack>
            </Stack>

            <DialogFooter>
                <DefaultButton text="Tutup" onClick={props.onDismiss} />
            </DialogFooter>
        </Dialog>
    );
};

export default OfferHistoryModal;
